/* Work sites (industry pack): projects, client sites, properties, warehouses and branches employees are allocated to.
 * Same rules as api/internal/api/h_sites.go (contracts/API.md "Work sites"). */
import { isISODate } from "../../domain/dates";
import { round2 } from "../../domain/payroll";
import type * as T from "../types";
import { E, has, isNum, type Ctx } from "./ctx";

export const SITE_KINDS: T.SiteKind[] = ["project", "client", "property", "warehouse", "branch"];

/** Validates the fields present in `m` and copies them onto `x` (throws 422 with every bad field). */
function applyPatch(c: Ctx, m: Record<string, unknown>, x: T.WorkSite): void {
  const fe: Record<string, string> = {};
  const text = (k: "name" | "nameAr" | "client" | "city", max: number, required: boolean) => {
    if (!has(m, k)) return;
    const raw = m[k];
    const v = typeof raw === "string" ? raw.trim() : "";
    if (typeof raw !== "string") fe[k] = "must be text";
    else if (required && !v) fe[k] = "required";
    else if ([...v].length > max) fe[k] = `max ${max} characters`;
    else x[k] = v;
  };
  text("name", 120, true);
  text("nameAr", 120, false);
  text("client", 120, false);
  text("city", 60, false);
  if (has(m, "kind")) {
    if (!SITE_KINDS.includes(m.kind as T.SiteKind)) fe.kind = "must be one of " + SITE_KINDS.join(", ");
    else x.kind = m.kind as T.SiteKind;
  }
  for (const k of ["start", "end"] as const) {
    if (!(k in m)) continue;
    const v = m[k];
    if (v != null && (typeof v !== "string" || (v !== "" && !isISODate(v)))) { fe[k] = "must be a date (YYYY-MM-DD)"; continue; }
    x[k] = (v ?? "") as T.ISODate;
  }
  if (!fe.start && !fe.end && x.start && x.end && x.end < x.start) fe.end = "must be on or after start";
  if (has(m, "allowance")) {
    const v = m.allowance;
    if (!isNum(v) || v < 0 || v > 100000) fe.allowance = "must be between 0 and 100000";
    else x.allowance = round2(v);
  }
  if (has(m, "contractValue")) {
    const v = m.contractValue;
    if (!isNum(v) || v < 0 || v > 100000000) fe.contractValue = "must be between 0 and 100000000";
    else x.contractValue = round2(v);
  }
  if (has(m, "outdoor")) {
    if (typeof m.outdoor !== "boolean") fe.outdoor = "must be true or false";
    else x.outdoor = m.outdoor;
  }
  if (has(m, "status")) {
    if (m.status !== "Active" && m.status !== "Closed") fe.status = "must be Active or Closed";
    else x.status = m.status;
  }
  if (has(m, "employeeIds")) {
    const arr = m.employeeIds;
    const ids: number[] = [];
    let ok = Array.isArray(arr);
    if (Array.isArray(arr)) {
      for (const v of arr) {
        if (typeof v !== "number" || !Number.isInteger(v)) { ok = false; break; }
        if (!ids.includes(v)) ids.push(v);
      }
    }
    if (!ok) fe.employeeIds = "must be a list of employee ids";
    else {
      const unknown = ids.find((id) => !c.state.employees.some((e) => e.id === id));
      if (unknown != null) fe.employeeIds = `unknown employee ${unknown}`;
      else x.employeeIds = ids;
    }
  }
  if (Object.keys(fe).length) throw E.validation(fe);
}

/** An employee works on one Active site at a time: remove x's employees from every other Active site. */
function releaseFromOtherSites(c: Ctx, x: T.WorkSite): void {
  if (x.status !== "Active" || !x.employeeIds.length) return;
  for (const o of c.state.sites) {
    if (o.id === x.id || o.status !== "Active") continue;
    o.employeeIds = o.employeeIds.filter((id) => !x.employeeIds.includes(id));
  }
}

const sorted = (xs: T.WorkSite[]) => xs.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

export function sitesOps(c: Ctx) {
  return {
    async listSites(): Promise<T.WorkSite[]> {
      c.principal("sites.read");
      return sorted(c.state.sites);
    },

    async createSite(input: T.WorkSiteInput): Promise<T.WorkSite> {
      const u = c.principal("hr.modules");
      if (input == null || typeof input !== "object" || Array.isArray(input)) throw E.badRequest("JSON object expected");
      const m: Record<string, unknown> = { ...input };
      if (!has(m, "name")) m.name = "";
      const x: T.WorkSite = { id: "", name: "", nameAr: "", kind: "project", client: "", city: "", start: "", end: "", outdoor: false, allowance: 0, contractValue: 0, status: "Active", employeeIds: [] };
      applyPatch(c, m, x);
      const n = c.nextId("sites", 1, () => c.state.sites.reduce((mx, s) => Math.max(mx, Number(/(\d+)$/.exec(s.id)?.[1] ?? 0)), 0));
      x.id = "SITE-" + String(n).padStart(4, "0");
      releaseFromOtherSites(c, x);
      c.state.sites.push(x);
      c.audit(u, "Added work site", "WorkSite", x.id, x.name);
      return x;
    },

    async updateSite(id: string, patch: T.WorkSiteInput): Promise<T.WorkSite> {
      const u = c.principal("hr.modules");
      if (patch == null || typeof patch !== "object" || Array.isArray(patch)) throw E.badRequest("JSON object expected");
      const cur = c.state.sites.find((s) => s.id === id);
      if (!cur) throw E.notFound("work site not found");
      const x = structuredClone(cur);
      applyPatch(c, patch as Record<string, unknown>, x);
      Object.assign(cur, x);
      releaseFromOtherSites(c, cur);
      c.audit(u, "Updated work site", "WorkSite", cur.id, Object.keys(patch).sort().join(", "));
      return cur;
    },
  };
}
